import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ROSEBAR } from "../lib/constants";
import ScrambleText from "./ScrambleText";

const PILL_MESSAGES = [
  { label: "Reservations open" },
  { label: "Guest Appearances" },
];

// Detect mobile once — desktop keeps the full futuristic show, mobile gets a lighter version
const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
const EMBER_COUNT = isMobile ? 0 : 22;

export default function Hero() {
  const [pillIdx, setPillIdx] = useState(0);
  const titleRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setPillIdx((i) => (i + 1) % PILL_MESSAGES.length), 3200);
    return () => clearInterval(id);
  }, []);

  // Mouse-parallax tilt on the headline (desktop only)
  useEffect(() => {
    const el = titleRef.current;
    if (!el || window.matchMedia("(pointer:coarse)").matches) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / rect.width;
      const dy = (e.clientY - cy) / rect.height;
      el.style.transform = `perspective(1200px) rotateX(${dy * -4}deg) rotateY(${dx * 6}deg) translateZ(0)`;
    };
    const onLeave = () => {
      el.style.transform = "perspective(1200px) rotateX(0) rotateY(0)";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden flex items-center justify-center" data-testid="hero-section">
      {/* Aurora + scanline + ember particles for futuristic depth */}
      <div className="hero-aurora" aria-hidden />
      <div className="hero-scanline" aria-hidden />
      <div className="hero-embers" aria-hidden>
        {Array.from({ length: EMBER_COUNT }).map((_, i) => (
          <span key={i} style={{ "--i": i, "--d": `${(i * 0.5) % 5}s`, "--x": `${(i * 37) % 100}%` }} />
        ))}
      </div>

      {/* Ambient gradients */}
      <div className="ambient-rose" style={{ left: "-180px", top: "-120px", background: "radial-gradient(circle, #B76E79 0%, transparent 70%)" }} />
      <div className="ambient-rose" style={{ right: "-180px", bottom: "-120px", background: "radial-gradient(circle, #D4AF7A 0%, transparent 70%)", opacity: 0.25 }} />

      {/* Faint grid */}
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black" />

      <div className="relative z-10 max-w-[1100px] mx-auto px-6 pt-28 pb-20 text-center">
        <div
          className="status-pill mx-auto mb-10 transition-opacity duration-500"
          data-testid="hero-status-pill"
          key={pillIdx}
          style={{ animation: "pill-swap 0.55s cubic-bezier(0.2,0.7,0.2,1)" }}
        >
          {PILL_MESSAGES[pillIdx].label}
        </div>

        <h1
          ref={titleRef}
          className="font-display text-[36px] sm:text-7xl md:text-[92px] lg:text-[108px] leading-[0.95] text-white tracking-tighter hero-title-3d uppercase"
          data-testid="hero-title"
        >
          <span className="block whitespace-nowrap">
            {isMobile ? (
              <span className="hero-line-top inline-block">WHERE DC'S ELITE</span>
            ) : (
              <ScrambleText delay={150} perChar={42} className="hero-line-top inline-block" testid="hero-line-top">
                WHERE DC'S ELITE
              </ScrambleText>
            )}
          </span>
          <span className="block mt-1 relative whitespace-nowrap">
            <span className="font-display-italic hero-holo inline-block normal-case" data-testid="hero-italic">
              {isMobile ? (
                "come out to play."
              ) : (
                <ScrambleText delay={950} perChar={48}>
                  come out to play.
                </ScrambleText>
              )}
            </span>
            <span className="hero-underline" aria-hidden />
          </span>
        </h1>

        <p className="mt-8 text-base md:text-lg text-white/65 max-w-2xl mx-auto leading-relaxed hero-fade-up" style={{ animationDelay: "1.6s" }}>
          {ROSEBAR.tagline}. Exclusive tables, celebrity nights, and the bottle service experience DC was built for — in the heart of Dupont Circle.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center hero-fade-up" style={{ animationDelay: "1.85s" }}>
          <Link to="/reservations" className="btn-primary" data-testid="hero-reserve-btn">
            Reserve VIP Table <ArrowRight size={15} />
          </Link>
          <Link to="/events" className="btn-secondary" data-testid="hero-tickets-btn">
            General Admission
          </Link>
        </div>
      </div>
    </section>
  );
}
